import React, { useEffect, useMemo, useRef, useState } from "react";
import Joi from "joi-browser";
import moment from "moment";
import Modal from "react-bootstrap/Modal";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import careTeamOptionSets from "../../options/careteam.json";
import contactOptionSets from "../../options/contact.json";
import generalOptionSets from "../../options/general.json";
import {
  getReferringAgencies,
  getUsers,
  searchContact,
} from "../services/soar";
import {
  checkKeyPress,
  createEntity,
  formatDate,
  getFieldDate,
  getFieldName,
  getFieldText,
  updateEntity,
} from "./../../scripts/helperScripts";
import {
  Checkbox,
  Input,
  PhoneInput,
  RadioButton,
  SearchBox,
  Select,
} from "./ControlBundle";

function CareTeamModal({
  isOpen,
  hideModal,
  nextModal,
  isLastModal,
  initialData,
  clientProgram,
  existingCareTeam,
  updateCareTeam,
}) {
  const clientProgramAdmitDate = useMemo(() => {
    return clientProgram?.ccx_admitdate
      ? new Date(getFieldDate(clientProgram.ccx_admitdate))
      : new Date();
  }, [clientProgram?.ccx_admitdate]);
  const [careTeam, setCareTeam] = useState({});
  const [providers, setProviders] = useState();
  const [primaryProvider, setPrimaryProvider] = useState({});
  const [providerSearchQuery, setProviderSearchQuery] = useState("");
  const [collateralContact, setCollateralContact] = useState();
  const [collateralContactErrors, setCollateralContactErrors] = useState({});
  const [collateralOptions, setCollateralOptions] = useState([]);
  const [collateralSearchQuery, setCollateralSearchQuery] = useState("");
  const [isCollateralLoading, setIsCollateralLoading] = useState(false);
  const [countyOptions, setCountyOptions] = useState([]);
  const [showAddContact, setShowAddContact] = useState(false);
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  const [errors, setErrors] = useState({});
  const isMountedRef = useRef(true);
  const navigate = useNavigate();

  //Validation Schema
  const careTeamSchema = useMemo(() => {
    return {
      ccx_careteamid: Joi.string().allow(null, "").label("Care Team Id"),
      ccx_membertype: Joi.string().required().label("Member Type"),
      ccx_memberid: Joi.string().label("User").when("ccx_membertype", {
        is: "803080002",
        then: Joi.string().required(),
      }),
      sor_providerrole: Joi.string().label("Role").when("ccx_membertype", {
        is: "803080002",
        then: Joi.string().required(),
      }),
      ccx_primarymember: Joi.string()
        .label("Primary Member")
        .when("ccx_membertype", { is: "803080002", then: Joi.required() }),
      ccx_contact: Joi.string().label("Collateral").when("ccx_membertype", {
        is: "803080001",
        then: Joi.string().required(),
      }),
      sor_contactrole: Joi.string().label("Role").when("ccx_membertype", {
        is: "803080001",
        then: Joi.string().required(),
      }),
      sor_contactrelationshipother: Joi.string()
        .label("Other Relationship details")
        .when("sor_contactrole", { is: "186460009", then: Joi.required() }),
      sor_emergencycontact: Joi.string().when("ccx_membertype", {
        is: "803080001",
        then: Joi.required(),
      }),
      ccx_effdate: Joi.date()
        .required()
        .min(moment(clientProgramAdmitDate).subtract(1, "day").toDate())
        .max(
          moment(clientProgramAdmitDate).isAfter(moment().add(5, "days"))
            ? moment(clientProgramAdmitDate).toDate()
            : moment().add(5, "days").toDate()
        )
        .label("Effective Date")
        .error((errors) => {
          errors.forEach((error) => {
            switch (error.type) {
              case "date.base":
                error.message = `Effective Date is required`;
                break;
              case "date.max":
                error.message = `Effective Date cannot be future dated beyond ${moment(
                  error.context.limit
                ).format("L")}`;
                break;
              case "date.min":
                error.message = `Effective Date cannot be before ${moment(
                  error.context.limit
                )
                  .add(1, "d")
                  .format("L")}`;
                break;
              default:
                error.message = `Effective Date is invalid`;
                break;
            }
          });
          return errors;
        }),
      ccx_expdate: Joi.alternatives([
        Joi.date()
          .max(
            new Date(careTeam.ccx_effdate) > new Date()
              ? new Date(careTeam.ccx_effdate)
              : "now"
          )
          .min(new Date(careTeam.ccx_effdate))
          .allow(null)
          .label("Expiration Date")
          .error((errors) => {
            errors.forEach((error) => {
              switch (error.type) {
                case "date.min":
                  error.message = `Expiration Date needs to be on or after Effective Date of ${moment
                    .utc(error.context.limit)
                    .format("L")}`;
                  break;
                case "date.max":
                  error.message = `Expiration Date cannot be future dated beyond Effective Date of ${moment
                    .utc(error.context.limit)
                    .format("L")}`;
                  break;
                default:
                  error.message = `Expiration Date is invalid`;
                  break;
              }
            });
            return errors;
          }),
        Joi.string().valid(""),
      ]),
      ccx_expirationreason: Joi.string()
        .allow(null, "")
        .label("Expiration Reason"),
    };
  }, [clientProgramAdmitDate, careTeam.ccx_effdate]);

  const contactSchema = {
    FirstName: Joi.string().label("First Name").required(),
    LastName: Joi.string().label("Last Name").required(),
    Email: Joi.string().label("Email").required(),
    MobilePhone: Joi.string().label("Mobile Phone").required(),
    Gender: Joi.string().label("Gender").required(),
    DateOfBirth: Joi.string().label("Date of Birth").required(),
    Type: Joi.string().label("Contact Type").required(),
    County: Joi.string().label("County").allow(""), //.when("Type", {is: "229580006", then: Joi.required()}),
  };

  useEffect(() => {
    const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));

    if (!safyInfo) navigate("/");
  });

  useEffect(() => {
    return () => (isMountedRef.current = false);
  }, []);

  useEffect(() => {
    if (
      clientProgram === undefined ||
      clientProgram.ccx_company === undefined
    ) {
      return;
    }

    const loadCountyOptions = async () => {
      const response = await getReferringAgencies(
        getFieldName(clientProgram.ccx_company).replace(" Contact Owner", "")
      );

      if (!isMountedRef.current || !response) {
        return;
      }

      const options = response.map((agency) => {
        return { _id: agency.accountid, name: agency.name };
      });

      setCountyOptions(options);
    };

    loadCountyOptions();
  }, [clientProgram]);

  useEffect(() => {
    if (!initialData) {
      const loadUsers = async () => {
        const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
        const response = await getUsers();

        if (!isMountedRef.current || !response) {
          return;
        }

        const businessUnitUsers = response.filter((user) => {
          return (
            safyInfo.user.safyInfo.BusinessUnitIdName === "ccplive" ||
            getFieldText(user.businessunitid) ===
              `{${safyInfo.user.safyInfo.BusinessUnitId}}`
          );
        });

        setProviders(businessUnitUsers);
      };

      loadUsers();

      const obj = {
        ccx_membertype: "803080002",
        ccx_effdate: moment().format(moment.HTML5_FMT.DATE),
        ccx_expdate: null,
        ccx_expirationreason: null,
        ccx_primarymember: "0",
        sor_emergencycontact: "0",
      };
      setCareTeam(obj);
      return;
    }

    const obj = { ...initialData };

    if (obj.ccx_contact) {
      setCollateralOptions([
        {
          _id: obj.ccx_contact,
          name: obj.ContactName,
          fullname: obj.ContactName,
        },
      ]);
    } else if (obj.ccx_memberid) {
      setProviders([
        {
          systemuserid: obj.ccx_memberid,
          fullname: obj.ProviderName,
        },
      ]);
    }

    delete obj.ContactName;
    delete obj.ProviderName;
    setCareTeam(obj);
  }, [initialData]);

  useEffect(() => {
    if (collateralContact !== undefined) {
      setCollateralOptions([
        {
          _id: getFieldText(collateralContact),
          name: getFieldName(collateralContact),
          fullname: getFieldName(collateralContact),
        },
      ]);
    }
  }, [collateralContact]);

  useEffect(() => {
    if (existingCareTeam !== undefined) {
      const primaryMember = existingCareTeam.filter(
        (ct) =>
          getFieldText(ct.ccx_primarymember) === "1" &&
          getFieldText(ct.ccx_clientprogram) ===
            clientProgram.ccx_client_programid &&
          new Date(getFieldDate(ct.ccx_effdate)) <= new Date() &&
          (ct.ccx_expdate === undefined ||
            new Date(getFieldDate(ct.ccx_expdate)) >
              new Date().setHours(0, 0, 0, 0))
      )[0];

      if (
        primaryMember !== undefined &&
        primaryMember.ccx_careteamid !== careTeam.ccx_careteamid
      )
        setPrimaryProvider(primaryMember);
    }
  }, [
    existingCareTeam,
    careTeam.ccx_careteamid,
    clientProgram.ccx_client_programid,
  ]);

  const filterUsers = () => {
    if (!providers) {
      return [];
    }

    let filtered = providers;
    if (providerSearchQuery) {
      filtered = filtered.filter(
        (user) =>
          user.fullname &&
          user.fullname
            .toLowerCase()
            .includes(providerSearchQuery.toLowerCase())
      );
    }
    const options = filtered.map((user) => {
      return {
        _id: user.systemuserid,
        name: user.fullname,
      };
    });
    return options;
  };

  const handleSearch = ({ currentTarget: input }) => {
    setProviderSearchQuery(input.value);
  };

  const handleCollateralSearch = async () => {
    const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
    setIsCollateralLoading(true);
    const response = await searchContact(
      collateralSearchQuery,
      safyInfo.user.safyInfo.BusinessUnitIdName
    );

    if (!isMountedRef.current) {
      return;
    }

    if (!response) {
      setCollateralOptions([]);
      setIsCollateralLoading(false);
      return;
    }

    const options = response.map((contact) => {
      return {
        _id: contact.contactid,
        name:
          contact.fullname +
          (contact.ccx_contacttype
            ? ` - ${getFieldName(contact.ccx_contacttype)}`
            : "") +
          (contact.birthdate ? ` - ${getFieldDate(contact.birthdate)}` : ""),
        fullname: contact.fullname,
        email: contact.emailaddress1,
        phone: contact.mobilephone,
      };
    });

    setCollateralOptions(options);
    setIsCollateralLoading(false);
  };

  const handleChange = ({ currentTarget: input }) => {
    const inputObj = {
      name: input.name,
      type: input.type,
      value:
        input.type === "checkbox" ? (input.checked ? "1" : "0") : input.value,
    };

    if (inputObj.name === "ccx_membertype") {
      setErrors({});
      const data = { ...careTeam };
      data[inputObj.name] = inputObj.value;
      setCareTeam(data);
    } else {
      const errs = { ...errors };
      const errorMessage = validateProperty(inputObj, careTeamSchema);

      if (errorMessage) {
        errs[inputObj.name] = errorMessage;
      } else {
        delete errs[inputObj.name];
      }

      delete errs.ExistingCareTeam;
      const data = { ...careTeam };
      data[inputObj.name] = inputObj.value;
      setCareTeam(data);
      setErrors(errs);
    }
  };

  const handleAddContact = () => {
    setCollateralContact({
      FirstName: "",
      LastName: "",
      Type: "",
      Email: "",
      MobilePhone: "",
      DateOfBirth: "",
      Gender: "",
      County: "",
    });
    setShowAddContact(true);
  };

  const handleChangeContact = ({ currentTarget: input }) => {
    const errs = { ...collateralContactErrors };
    const errorMessage = validateProperty(input, contactSchema);

    if (errorMessage) {
      errs[input.name] = errorMessage;
    } else {
      delete errs[input.name];
    }
    setCollateralContactErrors(errs);

    const data = { ...collateralContact };
    data[input.name] = input.value;
    setCollateralContact(data);
  };

  const validateProperty = (input, joiSchema) => {
    const { name, value } = input;

    console.log("validateProperty: ", clientProgramAdmitDate);
    const obj = { [name]: value };
    const schema = { [name]: joiSchema[name] };
    const { error } = Joi.validate(obj, schema);
    return error ? error.details[0].message : null;
  };

  const validate = () => {
    const options = { abortEarly: false };
    let error = Joi.validate(careTeam, careTeamSchema, options)?.error;

    if (!error?.details) {
      error = { details: [] };
    }

    const validationErrors = {};
    for (let item of error.details)
      validationErrors[item.path[0]] = item.message;

    if (
      careTeam.ccx_membertype === "803080001" &&
      !careTeam.ccx_careteamid &&
      existingCareTeam
    ) {
      const selectedProgramCareTeam = existingCareTeam.filter((ct) => {
        if (
          getFieldText(ct.ccx_clientprogram) !==
            clientProgram.ccx_client_programid ||
          getFieldText(ct.ccx_contact) !== careTeam.ccx_contact
        ) {
          return false;
        }

        if (!ct.ccx_expdate) {
          return true;
        }

        const existingExpDate = moment(getFieldDate(ct.ccx_expdate));
        const existingEffDate = moment(getFieldDate(ct.ccx_effdate));
        const newEffDate = moment(careTeam.ccx_effdate);

        if (
          newEffDate.isAfter(existingEffDate) &&
          newEffDate.isBefore(existingExpDate)
        ) {
          return true;
        }

        if (!careTeam.ccx_expdate) {
          return false;
        }

        const newExpDate = careTeam.ccx_expdate
          ? moment(careTeam.ccx_expdate)
          : moment();

        if (
          newExpDate.isAfter(existingEffDate) &&
          newExpDate.isBefore(existingExpDate)
        ) {
          return true;
        }

        return false;
      });

      if (selectedProgramCareTeam.length > 0) {
        toast.warning(
          "This Member already exists on the Care Team for the desired date range."
        );
        validationErrors.ExistingCareTeam =
          "This Member already exists on the Care Team for the desired date range.";
      }
    }

    setErrors(validationErrors);
    return Object.keys(validationErrors).length > 0
      ? validationErrors
      : undefined;
  };

  const handleSave = () => {
    const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
    setIsSaveInProgress(true);

    const obj = {
      ccx_client: {
        type: "entity",
        value: {
          entityName: "contact",
          entityId:
            clientProgram.ccx_patient &&
            getFieldText(clientProgram.ccx_patient),
        },
      },
      ccx_clientcompanyid: {
        type: "entity",
        value: {
          entityName: "ccx_clientagency",
          entityId:
            clientProgram.ccx_company &&
            getFieldText(clientProgram.ccx_company),
        },
      },
      ccx_clientprogram: {
        type: "entity",
        value: {
          entityName: "ccx_client_program",
          entityId: clientProgram.ccx_client_programid,
        },
      },
      ccx_membertype: {
        type: "optionset",
        value: careTeam.ccx_membertype
          ? parseInt(careTeam.ccx_membertype)
          : null,
      },
      ccx_effdate: {
        type: "datetime",
        value: careTeam.ccx_effdate ? new Date(careTeam.ccx_effdate) : null,
      },
      ccx_expdate: {
        type: "datetime",
        value: careTeam.ccx_expdate ? new Date(careTeam.ccx_expdate) : null,
      },
      ccx_expirationreason: {
        type: "optionset",
        value: careTeam.ccx_expirationreason
          ? parseInt(careTeam.ccx_expirationreason)
          : null,
      },
      ccx_role: {
        type: "optionset",
        value: 803080000,
      },
    };

    if (careTeam.ccx_membertype === "803080001") {
      const contact = collateralOptions.filter(
        (option) => option._id === careTeam.ccx_contact
      )[0];
      obj.ccx_name = {
        type: "string",
        value: `${getFieldName(clientProgram.ccx_patient)}: ${
          contact.fullname
        } (${moment.utc(new Date(careTeam.ccx_effdate)).format("MM/DD/YYYY")}-${
          careTeam.ccx_expdate
            ? moment.utc(new Date(careTeam.ccx_expdate)).format("MM/DD/YYYY")
            : "no date"
        })`,
      };
      obj.ccx_contact = {
        type: "entity",
        value: {
          entityName: "contact",
          entityId: careTeam.ccx_contact ? careTeam.ccx_contact : null,
        },
      };
      obj.sor_contactrole = {
        type: "optionset",
        value: careTeam.sor_contactrole
          ? parseInt(careTeam.sor_contactrole)
          : null,
      };

      if (careTeam.sor_contactrole === "186460009") {
        // Other Relationship
        obj.sor_contactrelationshipother = {
          type: "string",
          value: careTeam.sor_contactrelationshipother,
        };
      }

      obj.sor_emergencycontact = {
        type: "bool",
        value: careTeam.sor_emergencycontact === "1",
      };
    } else if (careTeam.ccx_membertype === "803080002") {
      let selectedProgramCareTeam = [];
      if (existingCareTeam) {
        selectedProgramCareTeam = existingCareTeam.filter(
          (ct) =>
            getFieldText(ct.ccx_clientprogram) ===
              clientProgram.ccx_client_programid &&
            new Date(getFieldDate(ct.ccx_effdate)) <= new Date() &&
            (ct.ccx_expdate === undefined ||
              new Date(getFieldDate(ct.ccx_expdate)) >=
                new Date().setHours(0, 0, 0, 0))
        );
      }

      if (careTeam.ccx_careteamid === undefined) {
        const existingCareTeamProviders = selectedProgramCareTeam.filter(
          (ct) =>
            getFieldText(ct.ccx_clientprogram) ===
              clientProgram.ccx_client_programid &&
            getFieldText(ct.ccx_memberid) === careTeam.ccx_memberid &&
            new Date(getFieldDate(ct.ccx_effdate)) <= new Date() &&
            (ct.ccx_expdate === undefined ||
              new Date(getFieldDate(ct.ccx_expdate)) >=
                new Date().setHours(0, 0, 0, 0))
        );

        if (existingCareTeamProviders.length > 0) {
          toast.warning(
            "This Member already exists on the Care Team for the desired date range."
          );
          return;
        }
      }

      obj.ccx_primarymember = {
        type: "bool",
        value: careTeam.ccx_primarymember === "1",
      };

      const provider = providers.filter(
        (p) => p.systemuserid === careTeam.ccx_memberid
      )[0];

      obj.ccx_name = {
        type: "string",
        value: `${getFieldName(clientProgram.ccx_patient)}: ${
          provider.fullname
        } (${moment.utc(new Date(careTeam.ccx_effdate)).format("MM/DD/YYYY")}-${
          careTeam.ccx_expdate
            ? moment.utc(new Date(careTeam.ccx_expdate)).format("MM/DD/YYYY")
            : "no date"
        })`,
      };
      obj.ccx_memberid = {
        type: "entity",
        value: {
          entityName: "systemuser",
          entityId: careTeam.ccx_memberid ? careTeam.ccx_memberid : null,
        },
      };
      obj.sor_providerrole = {
        type: "optionset",
        value: careTeam.sor_providerrole
          ? parseInt(careTeam.sor_providerrole)
          : null,
      };
    }
    if (careTeam.ccx_careteamid !== undefined) {
      const toastId = toast.loading("Care Team Member is being updated...", {
        type: toast.TYPE.INFO,
      });
      updateEntity(
        "ccx_careteam",
        careTeam.ccx_careteamid,
        JSON.stringify(obj),
        safyInfo
      )
        .then(() => {
          toast.update(toastId, {
            render: "Care Team Member has been updated.",
            type: toast.TYPE.SUCCESS,
            autoClose: 5000,
            isLoading: false,
            closeOnClick: true,
          });
          updateCareTeam && updateCareTeam(true);
        })
        .catch(() => {
          toast.update(toastId, {
            render: "An error occurred while updating the Care Team Member.",
            type: toast.TYPE.ERROR,
            autoClose: 5000,
            isLoading: false,
            closeOnClick: true,
          });
        })
        .finally(() => {
          setIsSaveInProgress(false);
        });
    } else {
      const toastId = toast.loading("Care Team Member is being created...", {
        type: toast.TYPE.INFO,
      });
      createEntity(
        "ccx_careteam",
        JSON.stringify(obj),
        safyInfo,
        getFieldName(clientProgram.ccx_company).split(" of ")[1]
      )
        .then(() => {
          toast.update(toastId, {
            render: "Care Team Member has been created.",
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            autoClose: 5000,
            closeOnClick: true,
          });
          updateCareTeam && updateCareTeam(true);
        })
        .catch(() => {
          toast.update(toastId, {
            render: "An error occurred while creating the Care Team Member.",
            type: toast.TYPE.ERROR,
            isLoading: false,
            autoClose: 5000,
            closeOnClick: true,
          });
        })
        .finally(() => {
          if (isMountedRef.current) setIsSaveInProgress(false);
        });
    }
  };

  const handleSaveAndAdd = () => {
    handleSave();
    resetForm();
  };

  const resetForm = (saveMemberType = true) => {
    const obj = {
      ccx_membertype: saveMemberType ? careTeam.ccx_membertype : "803080002",
      ccx_memberid: undefined,
      sor_providerrole: undefined,
      ccx_contact: undefined,
      sor_contactrole: undefined,
      sor_contactrelationshipother: undefined,
      ccx_effdate: moment().format(moment.HTML5_FMT.DATE),
      ccx_expdate: null,
      ccx_expirationreason: null,
      ccx_primarymember: "0",
      sor_emergencycontact: "0",
    };
    setCareTeam(obj);
    setCollateralOptions([]);
    setProviderSearchQuery("");
    setErrors({});
  };

  const handleHideModal = () => {
    if (hideModal) {
      resetForm(false);
      hideModal();
    }
  };

  const handleSaveContact = () => {
    if (validateContact()) {
      const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
      const obj = {
        firstname: {
          type: "string",
          value: collateralContact.FirstName,
        },
        lastname: {
          type: "string",
          value: collateralContact.LastName,
        },
        ccx_contacttype: {
          type: "optionset",
          value: parseInt(collateralContact.Type),
        },
      };

      if (
        collateralContact.Email !== undefined &&
        collateralContact.Email !== ""
      ) {
        obj.emailaddress1 = {
          type: "string",
          value: collateralContact.Email,
        };
      }

      if (
        collateralContact.MobilePhone !== undefined &&
        collateralContact.MobilePhone !== ""
      ) {
        obj.mobilephone = {
          type: "string",
          value: collateralContact.MobilePhone,
        };
      }

      if (
        collateralContact.DateOfBirth !== undefined &&
        collateralContact.DateOfBirth !== ""
      ) {
        obj.birthdate = {
          type: "datetime",
          value: new Date(collateralContact.DateOfBirth),
        };
      }

      if (
        collateralContact.Gender !== undefined &&
        collateralContact.Gender !== ""
      ) {
        obj.sor_preferredgender = {
          type: "optionset",
          value: parseInt(collateralContact.Gender),
        };
      }

      if (
        collateralContact.County !== undefined &&
        collateralContact.County !== ""
      )
        obj.parentcustomerid = {
          type: "entity",
          value: {
            entityName: "account",
            entityId: collateralContact.County,
          },
        };

      const toastId = toast.loading("Contact is being created...", {
        type: toast.TYPE.INFO,
      });
      createEntity(
        "contact",
        JSON.stringify(obj),
        safyInfo,
        getFieldName(clientProgram.ccx_company).split(" of ")[1]
      )
        .then((response) => {
          toast.update(toastId, {
            render: "Contact has been created.",
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            autoClose: 5000,
            closeOnClick: true,
          });

          const contact = response.resultset.result;
          setCollateralOptions([
            {
              _id: contact.contactid,
              name:
                contact.fullname +
                (contact.ccx_contacttype
                  ? ` - ${getFieldName(contact.ccx_contacttype)}`
                  : "") +
                (contact.birthdate
                  ? ` - ${getFieldDate(contact.birthdate)}`
                  : ""),
            },
          ]);
          const ct = { ...careTeam };
          ct.ccx_contact = contact.contactid;
          setCareTeam(ct);
          setShowAddContact(false);
          setCollateralContact(undefined);
        })
        .catch(() => {
          toast.update(toastId, {
            render: "An error occurred while creating the Contact.",
            type: toast.TYPE.ERROR,
            isLoading: false,
            autoClose: 5000,
            closeOnClick: true,
          });
        });
    }
  };

  const validateContact = () => {
    let validContact = true;
    const errs = { ...collateralContactErrors };

    if (collateralContact.FirstName === "") {
      errs.FirstName = "First Name is required";
      validContact = false;
    } else delete errs.FirstName;

    if (collateralContact.LastName === "") {
      errs.LastName = "Last Name is required";
      validContact = false;
    } else delete errs.LastName;

    if (collateralContact.Type === "") {
      errs.Type = "Contact Type is required";
      validContact = false;
    } else delete errs.Type;

    setCollateralContactErrors(errs);
    return validContact;
  };

  const handleKeyDown = (e) => {
    if (checkKeyPress(e)) {
      handleCollateralSearch();
    }
  };

  const selectedOption = useMemo(() => {
    return collateralOptions.find(
      (option) => option._id === careTeam.ccx_contact
    );
  }, [careTeam.ccx_contact, collateralOptions]);

  return (
    <>
      {isOpen ? (
        <Modal show={true} animation={false}>
          <Modal.Header>
            <Modal.Title>Care Team</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <RadioButton
              name="ccx_membertype"
              label="Member Type:"
              value={careTeam.ccx_membertype}
              options={careTeamOptionSets.ccx_membertype}
              defaultValue={
                initialData ? initialData.ccx_membertype : "803080002"
              }
              onChange={handleChange}
              disabled={careTeam.ccx_careteamid !== undefined}
              error={errors.ccx_membertype}
            />
            {careTeam.ccx_membertype === "803080001" && (
              <>
                <div className="container">
                  <div className="row">
                    <div className="col">
                      <label htmlFor="collateralSearch">
                        Search for Collateral
                      </label>
                      <input
                        id="collateralSearch"
                        name="collateralSearch"
                        value={collateralSearchQuery}
                        onChange={(e) => {
                          setCollateralSearchQuery(e.currentTarget.value);
                        }}
                        className="form-control"
                        placeholder="Search for Collateral"
                        disabled={careTeam.ccx_careteamid !== undefined}
                        onKeyDown={handleKeyDown}
                      />
                    </div>
                    <div className="col-auto">
                      <button
                        className="btn btn-primary me-2"
                        style={{ marginTop: "30px" }}
                        onClick={handleCollateralSearch}
                        disabled={careTeam.ccx_careteamid !== undefined}
                      >
                        Search
                      </button>
                    </div>
                  </div>
                </div>
                {isCollateralLoading && (
                  <div className="mb-2">Collateral is loading...</div>
                )}
                {!isCollateralLoading && (
                  <div className="container">
                    <div className="row">
                      <div className="col">
                        <Select
                          name="ccx_contact"
                          label="Collateral"
                          options={collateralOptions}
                          value={careTeam.ccx_contact}
                          onChange={handleChange}
                          error={errors.ccx_contact || errors.ExistingCareTeam}
                          disabled={
                            careTeam.ccx_careteamid !== undefined ||
                            collateralOptions.length === 0
                          }
                        />
                      </div>
                      <div className="col-auto">
                        <i
                          className="fas fa-plus-circle fa-lg me-2"
                          style={{
                            marginTop: "40px",
                            color: "rgba(0, 118, 168, 1)",
                          }}
                          onClick={handleAddContact}
                        ></i>
                      </div>
                    </div>
                  </div>
                )}
                {showAddContact && (
                  <>
                    <Input
                      name="FirstName"
                      label="First Name"
                      value={collateralContact.FirstName}
                      onChange={handleChangeContact}
                      error={collateralContactErrors.FirstName}
                    />
                    <Input
                      name="LastName"
                      label="Last Name"
                      value={collateralContact.LastName}
                      onChange={handleChangeContact}
                      error={collateralContactErrors.LastName}
                    />
                    <Input
                      name="Email"
                      label="Email"
                      value={collateralContact.Email}
                      onChange={handleChangeContact}
                      error={collateralContactErrors.Email}
                    />
                    <PhoneInput
                      name="MobilePhone"
                      label="Mobile Phone"
                      value={collateralContact.MobilePhone}
                      onChange={handleChangeContact}
                      error={collateralContactErrors.MobilePhone}
                    />
                    <Input
                      name="DateOfBirth"
                      label="Date of Birth"
                      value={collateralContact.DateOfBirth}
                      type="date"
                      onChange={handleChangeContact}
                    />
                    <Select
                      name="Gender"
                      label="Gender"
                      value={collateralContact.Gender}
                      options={contactOptionSets.sor_preferredgender}
                      onChange={handleChangeContact}
                    />
                    <Select
                      name="Type"
                      label="Contact Type"
                      value={collateralContact.Type}
                      options={contactOptionSets.ccx_contacttype}
                      onChange={handleChangeContact}
                      error={collateralContactErrors.Type}
                    />
                    {collateralContact.Type === "229580006" && (
                      <Select
                        name="County"
                        label="County"
                        value={collateralContact.County}
                        options={countyOptions}
                        onChange={handleChangeContact}
                        error={collateralContactErrors.County}
                      />
                    )}
                    <button
                      className="btn btn-danger mx-1"
                      onClick={() => setShowAddContact(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary mx-1"
                      onClick={() => {
                        if (validateContact()) handleSaveContact();
                      }}
                    >
                      Save Contact
                    </button>
                  </>
                )}
                {selectedOption?.email && <div>{selectedOption.email}</div>}
                {selectedOption?.phone && <div>{selectedOption.phone}</div>}
                {!showAddContact && (
                  <>
                    <Select
                      name="sor_contactrole"
                      label="Role"
                      options={careTeamOptionSets.sor_contactrole}
                      value={careTeam.sor_contactrole || ""}
                      error={errors.sor_contactrole}
                      onChange={handleChange}
                      disabled={careTeam.ccx_careteamid !== undefined}
                    />
                    {careTeam.sor_contactrole === "186460009" ? (
                      <Input
                        name="sor_contactrelationshipother"
                        label="Relationship details"
                        value={careTeam.sor_contactrelationshipother}
                        error={errors.sor_contactrelationshipother}
                        onChange={handleChange}
                        disabled={careTeam.ccx_careteamid !== undefined}
                      />
                    ) : null}
                    <Checkbox
                      name="sor_emergencycontact"
                      label="Emergency Contact"
                      checked={careTeam.sor_emergencycontact === "1"}
                      error={errors.sor_emergencycontact}
                      onChange={handleChange}
                      disabled={careTeam.ccx_careteamid !== undefined}
                      divClassName="ms-3"
                    />
                    <Input
                      name="ccx_effdate"
                      label="Effective Date"
                      type="date"
                      value={careTeam.ccx_effdate}
                      error={errors.ccx_effdate}
                      onChange={handleChange}
                      disabled={
                        !clientProgramAdmitDate ||
                        careTeam.ccx_careteamid !== undefined
                      }
                    />
                    {careTeam.ccx_careteamid && (
                      <>
                        <Input
                          name="ccx_expdate"
                          label="Expiration Date"
                          type="date"
                          defaultValue={
                            careTeam.ccx_expdate &&
                            formatDate(careTeam.ccx_expdate)
                          }
                          error={errors.ccx_expdate}
                          onChange={handleChange}
                        />
                        <Select
                          name="ccx_expirationreason"
                          label="Expiration Reason"
                          options={generalOptionSets.ccx_expirationreason}
                          value={careTeam.ccx_expirationreason}
                          error={errors.ccx_expirationreason}
                          onChange={handleChange}
                        />
                      </>
                    )}
                  </>
                )}
              </>
            )}
            {careTeam.ccx_membertype === "803080002" && (
              <div>
                <SearchBox
                  label="Search by Provider Name"
                  value={providerSearchQuery}
                  onChange={handleSearch}
                  clearSearch={() => setProviderSearchQuery("")}
                  placeholder="Search by Provider Name"
                ></SearchBox>
                <Select
                  name="ccx_memberid"
                  label="User"
                  options={filterUsers()}
                  value={careTeam.ccx_memberid || ""}
                  error={errors.ccx_memberid}
                  onChange={handleChange}
                  disabled={
                    providers === undefined ||
                    careTeam.ccx_careteamid !== undefined
                  }
                />
                <Select
                  name="sor_providerrole"
                  label="Role"
                  options={careTeamOptionSets.sor_providerrole}
                  value={careTeam.sor_providerrole || ""}
                  error={errors.sor_providerrole}
                  onChange={handleChange}
                  disabled={careTeam.ccx_careteamid !== undefined}
                />
                <RadioButton
                  name="ccx_primarymember"
                  label="Primary Member:"
                  value={careTeam.ccx_primarymember}
                  options={careTeamOptionSets.ccx_primarymember}
                  error={errors.ccx_primarymember}
                  onChange={handleChange}
                  disabled={
                    careTeam.ccx_careteamid !== undefined ||
                    primaryProvider.ccx_careteamid !== undefined ||
                    isSaveInProgress
                  }
                />
                {primaryProvider.ccx_careteamid !== undefined ? (
                  <div className="alert alert-warning">
                    The primary Care Team provider has already been set to{" "}
                    {primaryProvider["user.fullname"]}.
                  </div>
                ) : null}
                {clientProgram && (
                  <Input
                    name="ccx_effdate"
                    label="Effective Date"
                    type="date"
                    value={
                      (careTeam.ccx_effdate &&
                        moment
                          .utc(careTeam.ccx_effdate)
                          .format(moment.HTML5_FMT.DATE)) ||
                      ""
                    }
                    error={errors.ccx_effdate}
                    onChange={handleChange}
                    disabled={careTeam.ccx_careteamid !== undefined}
                  />
                )}
                {clientProgram === undefined ? (
                  <div>Client Program is being created...</div>
                ) : null}
                {careTeam.ccx_careteamid && (
                  <>
                    <Input
                      name="ccx_expdate"
                      label="Expiration Date"
                      type="date"
                      defaultValue={
                        (careTeam.ccx_expdate &&
                          moment
                            .utc(careTeam.ccx_expdate)
                            .format(moment.HTML5_FMT.DATE)) ||
                        ""
                      }
                      error={errors.ccx_expdate}
                      onChange={handleChange}
                    />
                    <Select
                      name="ccx_expirationreason"
                      label="Expiration Reason"
                      options={generalOptionSets.ccx_expirationreason}
                      value={careTeam.ccx_expirationreason}
                      error={errors.ccx_expirationreason}
                      onChange={handleChange}
                    />
                  </>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            {(!showAddContact || careTeam.ccx_membertype === "803080002") && (
              <>
                {nextModal === undefined && (
                  <>
                    <button
                      className="btn btn-danger"
                      onClick={handleHideModal}
                    >
                      Cancel
                    </button>
                    <button
                      className={
                        Object.keys(errors).length === 0
                          ? "btn btn-primary"
                          : "btn btn-secondary"
                      }
                      disabled={Object.keys(errors).length !== 0}
                      onClick={() => {
                        if (!validate()) {
                          handleSave();
                          handleHideModal();
                        }
                      }}
                    >
                      Submit
                    </button>
                    <button
                      className={
                        Object.keys(errors).length === 0
                          ? "btn btn-primary"
                          : "btn btn-secondary"
                      }
                      disabled={Object.keys(errors).length !== 0}
                      onClick={() => {
                        if (!validate()) handleSaveAndAdd();
                      }}
                    >
                      Submit and Add New
                    </button>
                  </>
                )}
                {nextModal && (
                  <>
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        resetForm();
                        nextModal();
                      }}
                    >
                      Skip
                    </button>
                    <button
                      className={
                        Object.keys(errors).length === 0
                          ? "btn btn-primary"
                          : "btn btn-secondary"
                      }
                      disabled={Object.keys(errors).length !== 0}
                      onClick={() => {
                        if (!validate()) {
                          handleSave();
                          resetForm();
                          nextModal();
                        }
                      }}
                    >
                      Submit and {isLastModal ? "End" : "Next"}
                    </button>
                    <button
                      className={
                        Object.keys(errors).length === 0
                          ? "btn btn-primary"
                          : "btn btn-secondary"
                      }
                      disabled={Object.keys(errors).length !== 0}
                      onClick={() => {
                        if (!validate()) handleSaveAndAdd();
                      }}
                    >
                      Submit and Add New
                    </button>
                  </>
                )}
              </>
            )}
          </Modal.Footer>
        </Modal>
      ) : null}
    </>
  );
}

export default CareTeamModal;
